<template>
  <div class="animated fadeIn card w-100">
    <div class="card-body view">
      <div class="header">
        <div class="title">
          <div>RDF</div>
        </div>
        <div class="steps">
          <el-steps :active="step" finish-status="success">
            <el-step
              v-for="step_index in Array.from(Array(steps_number).keys())"
              :title="`Step ${step_index + 1}`"
              :key="step_index"
            />
          </el-steps>
        </div>
      </div>
      <div class="form-wrapper">
        <Loader :show="fetchingData || loadingData" :fixed="false" style="z-index: 5" />
        <template v-if="!step && !fetchingData">
          <div class="form-row">
            <span>
              Study Name
              <span class="required-field">*</span>
            </span>
            <input
              v-model="localFormData.study_name"
              type="text"
              placeholder="Study name"
              class="title-input data-input ml-2"
            />
          </div>
          <div class="form-row">
            <span>
              Data input. Choose a CSV file
              <span class="required-field">*</span>
            </span>
            <el-upload
              class="upload-file el-upload-input-styles ml-2"
              :ref="`upload`"
              :show-file-list="false"
              :on-change="(f, flist) => handleChange(f, 'data_input')"
              :auto-upload="false"
              accept=".csv"
            >
              <el-input
                v-model="localFormData.data_input.name"
                class="el-input-upload"
                placeholder="Select or drop csv file"
                clearable
              />
            </el-upload>
          </div>
          <div class="form-row mb-1 w-100 text-left">
            <span class="ml-4 info-text-about-field">
              <span class="required-field">*</span>
              <span class="ml-3">- Required</span>
            </span>
          </div>
        </template>
        <template v-if="!loadingData && step === 1">
          <div class="form-row">
            <span>
              Upload likert scale input csv file
              <span class="required-field">*</span>
            </span>
            <el-upload
              class="upload-file el-upload-input-styles ml-2"
              :ref="`upload`"
              :show-file-list="false"
              :on-change="(f, flist) => handleChange(f, 'likert_scale')"
              :auto-upload="false"
              accept=".csv"
            >
              <el-input
                v-model="localFormData.likert_scale.name"
                class="el-input-upload"
                placeholder="Select or drop csv file"
                clearable
              />
            </el-upload>
          </div>
          <div class="form-row w-100" v-if="localFormData.folder_name">
            <button class="btn next-button ml-auto mr-auto" @click="downloadFile('scale_template.csv')">
              Download template
            </button>
          </div>
          <div class="form-row mb-1 w-100 text-left">
            <span class="ml-4 info-text-about-field">
              <span class="required-field">*</span>
              <span class="ml-3">- Required</span>
            </span>
          </div>
        </template>
        <template v-if="!loadingData && step === 2">
          <div class="form-row w-100 text-left" v-if="studyId">
            <span class="ml-4">Is the study paired? </span>
            <el-radio-group v-model="localFormData.paired" class="type-switch ml-auto mr-4">
              <el-radio-button :value="true">Paired (Complete Block)</el-radio-button>
              <el-radio-button :value="false">Unpaired (Incomplete Block)</el-radio-button>
            </el-radio-group>
          </div>
          <div class="form-row w-100 text-left">
            <span class="ml-4">Parametric or Non-Parametric test? </span>
            <el-radio-group v-model="localFormData.parametric" class="type-switch ml-auto mr-4">
              <el-radio-button :value="false">Non-Parametric</el-radio-button>
              <el-radio-button :value="true">Parametric</el-radio-button>
            </el-radio-group>
          </div>
          <div class="form-row w-100 text-left">
            <span class="ml-4">
              Select the type of tests needed.
              <span class="required-field">*</span>
            </span>
            <el-select
              class="type-input ml-auto mr-4"
              v-model="localFormData.test_types"
              multiple
              collapse-tags
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="test_type in test_types"
                  :key="test_type.value"
                  :label="test_type.label"
                  :value="test_type.value"
                >
                </el-option>
              </el-option-group>
            </el-select>
          </div>
          <div class="form-row mt-3 mb-1 w-100 text-left">
            <span class="ml-4 info-text-about-field">
              <span class="required-field">*</span>
              <span class="ml-3">- Required</span>
            </span>
          </div>
        </template>
        <template v-if="step === 3">
          <div class="form-row w-100 text-left">
            <span class="ml-4">
              Select the Product Code column
              <span class="required-field">*</span>
            </span>
            <el-select
              class="type-input ml-auto mr-4"
              v-model="localFormData.product_code"
              filterable
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="product_code in productCodesColumnsData"
                  :key="product_code.value"
                  :label="product_code.label"
                  :value="product_code.value"
                >
                </el-option>
              </el-option-group>
            </el-select>
          </div>
          <div class="form-row w-100 text-left">
            <span class="ml-4">
              Select the main question column (OOF)
              <span class="required-field">*</span>
              <span v-b-tooltip.hover.html="'Column that orders the product and will be the target dependent question for regression'">
                <font-awesome-icon icon="info-circle"/>
              </span>
            </span>
            <el-select
              class="type-input ml-auto mr-4"
              v-model="localFormData.main_question_column"
              filterable
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="metric in metricsData"
                  :key="metric.value"
                  :label="metric.label"
                  :value="metric.value"
                >
                </el-option>
              </el-option-group>
            </el-select>
          </div>
          <div class="form-row w-100 text-left">
            <span class="ml-4">
              Select how the order of the products will be displayed in the results
              <span class="required-field">*</span>
            </span>
            <el-select
              class="type-input ml-auto mr-4"
              v-model="localFormData.product_order"
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="product_order in product_order_options"
                  :key="product_order.value"
                  :label="product_order.value"
                  :value="product_order.value"
                >
                </el-option>
              </el-option-group>
            </el-select>
          </div>
          <!--    Section for product order, benchmark order and candidate order      -->
          <div class="form-row w-100 text-left show-animation" :class="{'show': localFormData.product_order === 'Specified Product Order'}">
            <span class="ml-4">
              Specify all product code order.
              <span class="required-field">*</span>
            </span>
            <el-select
              class="type-input ml-auto mr-4"
              v-model="localFormData.product_code_order"
              multiple
              collapse-tags
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="benchmark_product_code in productCodesData"
                  :key="benchmark_product_code.value"
                  :label="benchmark_product_code.value"
                  :value="benchmark_product_code.value"
                >
                </el-option>
              </el-option-group>
            </el-select>
          </div>
          <div class="form-row w-100 text-left show-animation" :class="{'show': (['Unspecified Product Order', 'Specified Product Order'].includes(localFormData.product_order))}">
            <span class="ml-4">
              Specify all benchmark product code.(Optional)
            </span>
            <el-select
              class="type-input ml-auto mr-4"
              v-model="localFormData.benchmark_product_code"
              multiple
              collapse-tags
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="benchmark_product_code in productCodesData"
                  :key="benchmark_product_code.value"
                  :label="benchmark_product_code.value"
                  :value="benchmark_product_code.value"
                >
                </el-option>
              </el-option-group>
            </el-select>
          </div>
          <div class="form-row w-100 text-left show-animation" :class="{'show': localFormData.product_order === 'Specified Benchmark Order Only'}">
            <span class="ml-4">
              Specify all benchmark product code order.
              <span class="required-field">*</span>
            </span>
            <el-select
              class="type-input ml-auto mr-4"
              v-model="localFormData.benchmark_product_code_order"
              multiple
              collapse-tags
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="benchmark_product_code in productCodesData"
                  :key="benchmark_product_code.value"
                  :label="benchmark_product_code.value"
                  :value="benchmark_product_code.value"
                >
                </el-option>
              </el-option-group>
            </el-select>
          </div>
          <div class="form-row w-100 text-left show-animation" :class="{'show': localFormData.product_order === 'Specified Candidate Order Only'}">
            <span class="ml-4">
              Specify all candidate product code order.
              <span class="required-field">*</span>
            </span>
            <el-select
              class="type-input ml-auto mr-4"
              v-model="localFormData.candidate_product_code_order"
              multiple
              collapse-tags
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="benchmark_product_code in productCodesData"
                  :key="benchmark_product_code.value"
                  :label="benchmark_product_code.value"
                  :value="benchmark_product_code.value"
                >
                </el-option>
              </el-option-group>
            </el-select>
          </div>

          <div class="form-row w-100 text-left">
            <span class="ml-4">
              Select the strength questions. (Optional)
            </span>
            <el-select
              class="type-input ml-auto mr-4"
              v-model="localFormData.strength_questions"
              multiple
              collapse-tags
              filterable
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="metric in metricsData"
                  :key="metric.value"
                  :label="metric.label"
                  :value="metric.value"
                >
                </el-option>
              </el-option-group>
            </el-select>
          </div>
          <div class="form-row w-100 text-left">
            <span class="ml-4">
              Select the
              <span v-b-tooltip.hover.html="'Consumer Perceived Value'"
                style="text-decoration: underline"
              >
                CPV
              </span>
              CPV questions. Only for 5 point scale. (Symart Panel only)
            </span>
            <el-select
              class="type-input ml-auto mr-4"
              v-model="localFormData.cpv_question"
              multiple
              collapse-tags
              filterable
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="metric in metricsData"
                  :key="metric.value"
                  :label="metric.label"
                  :value="metric.value"
                >
                </el-option>
              </el-option-group>
            </el-select>
          </div>
          <div class="form-row w-100 text-left">
            <span class="ml-4">
              What is the scale of the binary questions?
            </span>
            <el-select
              class="type-input ml-auto mr-4"
              v-model="localFormData.binary_option"
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="bq in binaryQuestionData"
                  :key="bq.value"
                  :label="bq.label"
                  :value="bq.value"
                >
                </el-option>
              </el-option-group>
            </el-select>
          </div>
          <div class="form-row mb-1 w-100 text-left">
            <span class="ml-4 info-text-about-field">
              <span class="required-field">*</span>
              <span class="ml-3">- Required</span>
            </span>
          </div>
        </template>
        <template v-if="step === 4">
          <div class="form-row">
            <span>Do you require regression?</span>
            <ToggleButton
              class="toggle-switch ml-2 custom-margin-top"
              style="margin-top: 10px"
              :labels="{checked: 'Yes', unchecked: 'No'}"
              :color="{checked: '#78bb95', unchecked: 'rgba(0, 0, 0, 0.15)'}"
              :sync="true"
              v-model="localFormData.regression"
            />
          </div>
          <div class="form-row show-animation" :class="{'show': localFormData.regression}">
            <div class="form-row file-upload-row w-100" v-if="localFormData.folder_name && !studyId">
            <span>
              Data input. Choose a CSV file
              <span class="required-field">*</span>
            </span>
            <el-upload
              class="upload-file el-upload-input-styles ml-2"
              :ref="`upload`"
              :show-file-list="false"
              :on-change="(f, flist) => handleChange(f, 'regression_input')"
              :auto-upload="false"
              accept=".csv"
            >
              <el-input
                v-model="localFormData.regression_input.name"
                class="el-input-upload"
                placeholder="Select or drop csv file"
                clearable
              />
            </el-upload>
            </div>
            <div class="form-row w-100" v-if="localFormData.folder_name && !studyId">
              <button class="btn next-button ml-auto mr-auto" @click="downloadFile('reg_template.csv')">
                Download template
              </button>
            </div>
          </div>
          <div class="form-row mb-1 w-100 text-left">
            <span class="ml-4 info-text-about-field">
              <span class="required-field">*</span>
              <span class="ml-3">- Required</span>
            </span>
          </div>
        </template>
        <template v-if="step === 5">
          <div class="form-row">
            <span>Do you need charts?</span>
            <ToggleButton
              class="toggle-switch ml-2 custom-margin-top"
              style="margin-top: 10px"
              :labels="{checked: 'Yes', unchecked: 'No'}"
              :color="{checked: '#78BB95', unchecked: 'rgba(0, 0, 0, 0.15)'}"
              :sync="true"
              v-model="localFormData.charts"
            />
          </div>
          <div class="form-row">
            <span>Replace product codes with names?</span>
            <ToggleButton
              class="toggle-switch ml-2 custom-margin-top"
              style="margin-top: 10px"
              :labels="{checked: 'Yes', unchecked: 'No'}"
              :color="{checked: '#78BB95', unchecked: 'rgba(0, 0, 0, 0.15)'}"
              :sync="true"
              v-model="localFormData.replace_product_codes"
            />
          </div>
          <div v-if="!studyId" class="form-row w-100 show-animation" :class="{'show no-show-margin': localFormData.replace_product_codes}">
            <div class="form-row file-upload-row w-100">
              <span>
                Upload the template after you have filled it
                <span class="required-field">*</span>
              </span>
              <el-upload
                class="upload-file el-upload-input-styles"
                :ref="`upload`"
                :show-file-list="false"
                :on-change="(f, flist) => handleChange(f, 'replace_product_input')"
                :auto-upload="false"
                accept=".csv"
              >
                <el-input
                  v-model="localFormData.replace_product_input.name"
                  class="el-input-upload"
                  placeholder="Select or drop csv file"
                  clearable
                />
              </el-upload>
            </div>
            <div class="form-row w-100" v-if="localFormData.folder_name && !studyId">
              <button class="btn next-button ml-auto mr-auto" @click="downloadFile('prod_template.csv')">
                Download template
              </button>
            </div>
          </div>
        </template>
        <Loader :show="loadingData && step === 6" :fixed="false" style="z-index: 5" />
        <template v-if="step === 6 && !loadingData && !rdfData">
          <div class="form-row mb-5">
            <h3>
              <i> No data </i>
            </h3>
          </div>
        </template>
        <template v-if="step === 6 && !loadingData && rdfData">
          <div class="search-filters-header">
            <div class="filter-tabs-wrapper">
              <div class="filters-tab" :class="{selected: openedPanel === 'tables'}" @click="openedPanel = 'tables'">
                TABLES
              </div>
              <div
                v-if="localFormData.charts"
                class="filters-tab"
                :class="{selected: openedPanel === 'charts'}"
                @click="openedPanel = 'charts'"
              >
                CHARTS
              </div>
            </div>
          </div>
          <div class="charts-container w-100 mt-3 d-flex flex-column text-center align-items-center" v-if="openedPanel === 'charts'">
            <span>Parsed chart data</span>
            <div v-if="rdfData.charts && rdfData.charts.length" class="d-flex flex-wrap flex-row align-items-center justify-content-center mt-1 charts-container">
              <div
                class="d-flex flex-column mb-4"
                v-for="(item, idx) in parsedChartData"
                :key="idx"
              >
                <h3 v-if="item.name">{{ item.name }}</h3>
                <BarChart
                  :chart-data="item"
                  :options="chart.options"
                  style="height: 300px;"
                />
              </div>
            </div>
            <div v-else class="d-flex flex-wrap flex-row align-items-center justify-content-center mt-1 charts-container">
              <h3>
                No data
              </h3>
            </div>
          </div>
          <div v-else class="charts-container w-100 text-center mt-3">
            <div class="form-row justify-center">
              <span class="ml-4 mr-auto">Filtered data salon line</span>
              <button class="ml-auto btn next-button" @click="downloadFile()">
                Download summary
              </button>
            </div>
            <div class="table-row text-center ml-auto mr-auto mb-4">
              <VClientTable
                class="hide-filters"
                :data="parsedSummary['data']"
                :columns="parsedSummary['headers']"
                :options="[]"
              >
                <template v-for="colSlot in parsedSummary['headers']" v-slot:[colSlot]="{ row }">
                  {{ row[colSlot] ? row[colSlot] : '-' }}
                </template>
              </VClientTable>
            </div>
            <div class="form-row justify-center">
              <span class="ml-4 mr-auto">Filtered data correlations</span>
              <button class="ml-auto btn next-button" @click="downloadFile()">
                Download correlation
              </button>
            </div>
            <div class="table-row text-center ml-auto mr-auto">
              <VClientTable
                class="hide-filters"
                :data="parsedRegressionData['data']"
                :columns="parsedRegressionData['headers']"
                :options="[]"
              >
                <template v-for="colSlot in parsedRegressionData['headers']" v-slot:[colSlot]="{ row }">
                  {{ row[colSlot] ? row[colSlot] : '-' }}
                </template>
              </VClientTable>
            </div>
          </div>
        </template>
      </div>
      <div>
        <div class="d-flex justify-content-end">
          <button
            class="btn next-button"
            @click="prevPage"
            :disabled="!step || (!startFromBeginning && step === 2)"
          >
            Prev
          </button>
          <button
            class="btn next-button"
            @click="nextPage"
            :disabled="step === 6">
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import ToggleButton from '@/components/shared/ToggleButton'
import BarChart from '../shared/BarChart'
import Loader from '@/components/shared/Loader'
import globalAlertMixin from '@/mixins/globalAlert'
import VClientTable from '@/components/shared/VClientTable'

export default {
  name: 'RDFFirstForm',
  emits: ['update:formData'],
  components: {
    ToggleButton,
    BarChart,
    Loader,
    VClientTable
  },
  mixins: [globalAlertMixin],
  props: {
    'handleChange': Function,
    'formData': Object,
    checkRequiredData: Function,
    startFromBeginning: Boolean,
    loadingData: Boolean,
    step: Number,
    fetchingData: Boolean,
    studyId: String,
    rdfData: Object,
    metricsData: Array,
    productCodesData: Array,
    productCodesColumnsData: Array,
    binaryQuestionData: Array,
    downloadFile: Function
  },
  data () {
    return {
      localFormData: { ...this.formData },
      steps_number: 6,
      chart: {
        options: {
          scales: {
            x: {
              stacked: true
            }
          },
          tooltips: {
            enabled: true,
            mode: 'label',
            callbacks: {
              title: function (tooltipItems, data) {
                let idx = tooltipItems[0].index
                return data.labels[idx]
              }
            }
          },
          barValueSpacing: 20,
          responsive: true,
          maintainAspectRatio: false
        },
        showFilters: true,
        hideOverlay: false,
        filters: {
          metadata: '',
          metadataType: ''
        }
      },
      openedPanel: 'tables',
      product_order_options: [{
        value: 'Unspecified Product Order'
      }, {
        value: 'Specified Product Order'
      }, {
        value: 'Specified Benchmark Order Only'
      }, {
        value: 'Specified Candidate Order Only'
      }],
      test_types: [{
        label: 'One-tailed 99%',
        value: 'one_99'
      }, {
        label: 'One-tailed 95%',
        value: 'one_95'
      }, {
        label: 'One-tailed 90%',
        value: 'one_90'
      }, {
        label: 'One-tailed 80%',
        value: 'one_80'
      }, {
        label: 'Two-tailed 99%',
        value: 'two_99'
      }, {
        label: 'Two-tailed 95%',
        value: 'two_95'
      }, {
        label: 'Two-tailed 90%',
        value: 'two_90'
      }, {
        label: 'Two-tailed 80%',
        value: 'two_80'
      }],
      binary_options: [
        'No binary questions',
        '0 and 1',
        '1 and 2'
      ],
      requiredDataFields: {
        0: [{
          fields: [
            { key: 'study_name', name: 'Study Name' },
            { key: 'data_input', name: 'Data input' }
          ]
        }],
        1: [{
          fields: [
            { key: 'likert_scale', name: 'Likert scale input' }
          ]
        }],
        2: [{
          fields: [
            { key: 'test_types', name: 'Type of tests' }
          ]
        }],
        3: [
          {
            fields: [
              { key: 'product_code', name: 'Product code' },
              { key: 'main_question_column', name: 'Main question' },
              { key: 'product_order', name: 'Display option' }
            ]
          },
          {
            fields: [
              { key: 'product_code_order', name: 'Product code order' }
            ],
            fieldToCheck: 'product_order',
            fieldValue: 'Specified Product Order'
          },
          {
            fields: [
              { key: 'benchmark_product_code_order', name: 'Benchmark product code order' }
            ],
            fieldToCheck: 'product_order',
            fieldValue: 'Specified Benchmark Order Only'
          },
          {
            fields: [
              { key: 'candidate_product_code_order', name: 'Candidate product code order' }
            ],
            fieldToCheck: 'product_order',
            fieldValue: 'Specified Candidate Order Only'
          }
        ],
        4: [
          {
            fields: [
              { key: 'regression_input', name: 'Regression file' }
            ],
            checkIfChecked: 'regression'
          }
        ],
        5: [
          {
            fields: [
              { key: 'metricType', name: 'Metric type' },
              { key: 'metric', name: 'Metric' },
              { key: 'aggregation', name: 'Aggregation' }
            ],
            checkIfChecked: 'display_correlation'
          },
          {
            fields: [
              { key: 'replace_product_input', name: 'Replace product codes file' }
            ],
            checkIfChecked: 'replace_product_codes'
          }
        ]
      }
    }
  },
  computed: {
    metric_types_test_array () {
      return Array.from(Array(10).keys()).map(e => { return { value: `Metric Type Test ${e + 1}` } })
    },
    metrics_test_array () {
      return Array.from(Array(10).keys()).map(e => { return { value: `Metric Test ${e + 1}` } })
    },
    aggregation_test_array () {
      return Array.from(Array(10).keys()).map(e => { return { value: `Aggregation Test ${e + 1}` } })
    },
    parsedChartData () {
      return this.rdfData.charts || []
    },
    parsedSummary () {
      return {
        'headers': (this.rdfData && this.rdfData.summary) ? this.rdfData.summary.headers : [],
        'data': (this.rdfData && this.rdfData.summary) ? this.rdfData.summary.data : []
      }
    },
    parsedRegressionData () {
      return {
        'headers': (this.rdfData && this.rdfData.correlation) ? this.rdfData.correlation.headers : [],
        'data': (this.rdfData && this.rdfData.correlation) ? this.rdfData.correlation.data : []
      }
    }
  },
  methods: {
    prevPage () {
      this.$emit('prev-page')
    },
    async nextPage () {
      if (this.validFormData()) {
        await this.$emit('next-page')
      }
    },
    validFormData () {
      const dataToValid = this.requiredDataFields[this.step]
      let errors = []
      for (const obj of dataToValid) {
        if (this.studyId && this.step === 5 && obj.checkIfChecked === 'replace_product_codes') {
          continue
        }
        errors = this.checkRequiredData(
          obj.fields || [],
          obj.checkIfChecked || '',
          obj.fieldToCheck || '',
          obj.fieldValue || ''
        )
        if (errors.length > 0) break
      }

      if (errors.length > 0) {
        const options = {
          duration: 5000,
          title: 'Missing values!',
          message: errors.join('\n'),
          type: 'error'
        }
        this.launchAlert(options)
      }
      return errors.length === 0
    }
  },
  watch: {
    // Compare by value: both branches assign a freshly spread object, so an
    // identity check can never be false and the pair never settles - each pass
    // allocates more objects and re-triggers the other watcher.
    formData: {
      deep: true,
      handler (value) {
        if (!_.isEqual(value, this.localFormData)) {
          this.localFormData = _.cloneDeep(value)
        }
      }
    },
    localFormData: {
      deep: true,
      handler (value) {
        if (!_.isEqual(value, this.formData)) {
          this.$emit('update:formData', _.cloneDeep(value))
        }
      }
    }
  }
}
</script>

<style lang="scss">

.el-notification {
  .el-notification__group {
    .el-notification__content {
      white-space: break-spaces;
    }
  }
}

.charts-container {
  max-height: 450px;
  overflow-y: scroll;
}
.show-animation {
  overflow: hidden;
  max-height: 0;
  transform: scaleY(0);
  transition: 0.3s linear;
  margin-bottom: 0 !important;
  &.show {
    max-height: 500px;
    transform: scaleY(1);
    transition: 0.3s linear;
    margin-bottom: 30px !important;
  }
  &.show.no-show-margin {
    margin-bottom: 0 !important;
  }
}
.checkbox-div {
  margin-left: 1rem;
  width: 400px;
  display: flex;
  label.checkbox-binary {
    display: inline !important;
    max-width: unset !important;
    width: auto !important;
    .label {
      width: unset !important;
      margin-left: 25px !important;
      display: inline !important;
    }
  }
}

</style>
<style lang="scss" scoped>
@import "../../assets/scss/variables";
.custom-margin-top {
  margin-top: 15px;
}
.hide-filters {
  .VueTables__search-field {
    display: none !important;
  }
  .vgt-global-search {
    display: none !important;
  }
  .after-filter-wrapper {
    width: 100%;
    justify-content: flex-end;
    .info-text {
      margin-right: 15px;
    }
  }
}
.required-field {
  color: $red-700;
}

.card {
  margin-bottom: 0;
  border: 0;
  width: 100%;
  background-color: rgba(255,255,255,0.93);
  max-width: 1275px;
  overflow-y: auto;
}

.info-text-about-field {
  font-weight: unset;
}

.file-upload-row {
  span {
    margin-left: auto;
    margin-right: unset;
  }
  div.upload-file {
    margin-right: auto;
    margin-left: .5rem;
  }
}
@media screen and (min-width: $break-lg) {
  .info-text-about-field {
      font-size: 12px;
  }
  .custom-margin-top {
    margin-top: 10px !important;
  }
  .card {
    max-height: 450px;
  }
}
@media screen and (min-width: $break-xl) {
  .info-text-about-field{
    font-size: 14px;
  }
  .custom-margin-top {
    margin-top: 12px !important;
  }
  .card {
    max-height: 550px;
  }
}
@media screen and (min-width: $break-xxl) {
  .info-text-about-field {
    font-size: 16px;
  }
  .custom-margin-top {
    margin-top: 15px !important;
  }
  .card {
    max-height: 650px;
  }
}
@media screen and (min-width: $break-xxxl) {
  .card {
    max-height: 670px;
  }
}

.table-row {
  width: 100%;
  max-width: 1250px;
  height: 450px;
  overflow-y: scroll;
  overflow-x: hidden;
}
.search-filters-header {
  height: 45px;
  width: 100%;
  border-left: 0;
  border-right: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-left: 34px;
  padding-top: 13px;
  position: relative;

  .header-title {
    width: 500px;
    height: 56px;
    font-family: 'Source Sans Pro', sans-serif;
    font-size: 45px;
    line-height: 30px;
    font-weight: 900;
    letter-spacing: 0.9px;
    text-transform: uppercase;
    color: white;
    white-space: nowrap;
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  .header-subtitle {
    height: 30px;
    color: white;
    font-family: 'Open Sans', sans-serif;
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 0;
    margin-bottom: 56px;
  }

  .filter-tabs-wrapper {
    display: flex;
    position: absolute;
    bottom: -1px;
    right: 0;
    z-index: 3;
    width: 100%;
    padding-right: 80px;

    .filters-tab {
      max-width: 240px;
      width: 100%;
      height: 44px;
      color: $dusty-blue;
      border: 1px solid #25232C;
      background-color: $silver;
      display: flex;
      justify-content: center;
      align-items: center;
      font-family: 'Source Sans Pro', sans-serif;
      font-size: 20px;
      line-height: 25px;
      letter-spacing: 0;
      cursor: pointer;
      border-left: 0;

      &:first-of-type {
        border-left: 1px solid #25232C;
      }
    }

    .saved-searches-toggle {
      position: absolute;
      width: 80px;
      transition: right 0.35s ease-in-out;
      background-color: #3981FF;
      bottom: 0;
      border-left: 1px solid #25232C;
      border-right: 1px solid #25232C;
    }

    .selected {
      border-bottom: 0;
      background-color: $light-grey !important;
      font-weight: 900;
      position: relative;

      &:before {
        content: '';
        position: absolute;
        bottom: 0;
        width: 80%;
        left: 10%; /*this centers it based on the above width*/
        border-bottom: 2px solid $dusty-blue;
      }
    }
  }
}

hr.space {
  margin: 1.5em 0;
  visibility: hidden;
}

.view {
  display: flex;
  flex-direction: column;
  padding: 0;
  width: 100%;

  .header {
    display: flex;
    justify-content: space-between;

    .steps {
      width: 370px;
      height: 62px;
      margin-top: 20px;
      margin-right: 29px;
    }
  }

  .form-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: 600;
    font-size: 20px;
    line-height: 33px;
    @media screen and (min-width: $break-lg) {
      font-size: 18px;
      line-height: 38px;
      margin: 1.5rem 0 0;
    }
    @media screen and (min-width: $break-xl) {
      font-size: 20px;
      line-height: 43px;
      margin: 2.5rem 0 0;
    }
    @media screen and (min-width: $break-xxl) {
      font-size: 24px;
      line-height: 48px;
      margin: 3rem 0 0;
    }
    letter-spacing: 0;
    color: $dusty-blue;
    margin: 20px 0;

    .form-row {
      margin-bottom: 30px;
    }

    .best-worst-switch {
      margin: 0 15px;
      border-radius: 4px;
      box-shadow: 0 1px 4px #00000026;
    }

    .type-switch {
      margin: 0 15px;
      border-radius: 4px;
      box-shadow: 0 1px 4px #00000026;
    }

    .number-input {
      margin-left: 20px;
      width: 98px !important;
      height: 43px !important;
      @media screen and (min-width: $break-lg) {
        height: 47px;
      }
      @media screen and (min-width: $break-xl) {
        height: 51px;
      }
      @media screen and (min-width: $break-xxl) {
        height: 55px;
      }
    }

    .type-input {
      margin-left: 20px;
      height: 43px !important;
      @media screen and (min-width: $break-lg) {
        height: 47px;
      }
      @media screen and (min-width: $break-xl) {
        height: 51px;
      }
      @media screen and (min-width: $break-xxl) {
        height: 55px;
      }
    }
  }

  .next-button {
    font-family: 'Open Sans', sans-serif;
    font-weight: 900;
    line-height: 30px;
    letter-spacing: 0;
    background-color: $blueberry;
    color: white;
    text-transform: uppercase;
    border-radius: 4px;
    margin-right: 30px;
    margin-bottom: 15px;
    width: 160px;
    height: 38px;
    padding: 1px 2px;
    @media screen and (min-width: $break-lg) {
      width: 180px;
      height: 40px;
      padding: 2px 3px;
      font-size: 12px;
    }
    @media screen and (min-width: $break-xl) {
      width: 200px;
      height: 42px;
      padding: 3px;
      font-size: 13px;
    }
    @media screen and (min-width: $break-xxl) {
      width: 240px;
      height: 44px;
      padding: 3px 4px;
      font-size: 16px;
    }
    transition: background-color 0.2s linear;
    &:hover {
      background-color: #9256D9;
    }
  }
}

.title {
  margin-top: 20px;
  margin-left: 32px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  font-family: 'Source Sans Pro', sans-serif;
  font-weight: 900;
  font-size: 30px;
  line-height: 24px;
  letter-spacing: 0.8px;
  @media screen and (min-width: $break-lg) {
    font-size: 35px;
    line-height: 26px;
    letter-spacing: 0.8px;
    margin-top: 23px;
    margin-left: 37px;
  }
  @media screen and (min-width: $break-xl) {
    font-size: 40px;
    line-height: 28px;
    letter-spacing: 0.9px;
    margin-top: 26px;
    margin-left: 42px;
  }
  @media screen and (min-width: $break-xxl) {
    font-size: 45px;
    line-height: 30px;
    letter-spacing: 0.9px;
    margin-top: 29px;
    margin-left: 47px;
  }
  color: $pewter;
  text-transform: uppercase;
}

.footnote {
  height: 44px;
  background: transparent linear-gradient(268deg, #C961C5 0%, #DEBFDD 100%) 0% 0% no-repeat padding-box;
  box-shadow: 0 3px 6px #00000029;
  opacity: 0.9;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  @media screen and (min-width: $break-lg) {
    height: 49px;
    span {
      font-size: 18px;
      line-height: 20px;
      margin-right: 50px;
    }
  }
  @media screen and (min-width: $break-xl) {
    height: 54px;
    span {
      font-size: 18px;
      line-height: 22px;
      margin-right: 55px;
    }
  }
  @media screen and (min-width: $break-xxl) {
    height: 59px;
    span {
      font-size: 20px;
      line-height: 25px;
      margin-right: 60px;
    }
  }

  span {
    font-family: 'Source Sans Pro', sans-serif;
    font-size: 16px;
    line-height: 18px;
    color: $charcoal;
    margin-right: 45px;
  }
}

.title svg {
  margin-right: 0.5em;
}

.checkbox-label {
  width: 150px !important;
  margin-left: auto;
  margin-right: auto;
}

.scroller {
  overflow-x: unset;
  overflow-y: unset;
  height: 250px;
  width: 1000px;
}

.fragrances-container {
  height: 250px;
}

.fragrances-wrapper {
  height: 100%;
  width: 100%;
}

.fragrance-data-cell {
  min-width: 250px;
  max-width: 250px;
}

.correlations-data-cell {
  min-width: 90px;
  max-width: 90px;
}

.fragrance-label {
  min-width: 120px;
  max-width: 120px;
}

.metric-value {
  text-align: center;
  width: auto;
  padding: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 8px;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
}
</style>
<style lang="scss">
@import "../../assets/scss/variables";

.number-input {
  .el-input__inner {
    height: 40px !important;
    font-size: 18px !important;
    line-height: 20px !important;
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: 400;
    letter-spacing: 0;
    color: $pewter
  }
}
.type-input {
  .el-input__inner {
    height: 40px !important;
    font-size: 18px !important;
    line-height: 20px !important;
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: 400;
    letter-spacing: 0;
    color: $pewter
  }
}

</style>
